import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const KEY = "meda_pwa_banner_dismissed";

/**
 * One-time, dismissible "Install Meda AI" banner shown after login.
 * Only appears on browsers that support installation (Chrome/Edge desktop +
 * Android). Self-hides on iOS, when already installed, or once dismissed.
 */
export default function InstallPwaBanner() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone || localStorage.getItem(KEY) === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return dismiss();
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* dismissed */
    }
    dismiss();
  };

  if (!show || !deferred) return null;

  return (
    <div
      data-testid="install-pwa-banner"
      className="flex items-center gap-3 border-b border-[#1877F2]/20 bg-[#1877F2]/5 px-4 py-2.5 sm:px-6"
    >
      <Download className="h-4 w-4 shrink-0 text-[#1877F2]" />
      <p className="min-w-0 flex-1 truncate text-xs font-medium text-[#0A2540]">
        Install Meda AI for faster access — open it like an app, no browser needed.
      </p>
      <button
        type="button"
        onClick={install}
        data-testid="install-pwa-banner-cta"
        className="shrink-0 rounded-full bg-[#1877F2] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#1465d8]"
      >
        Install
      </button>
      <button
        type="button"
        onClick={dismiss}
        data-testid="install-pwa-banner-dismiss"
        aria-label="Dismiss"
        className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
